import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function MobileBottomSheet({
  isOpen,
  onClose,
  title,
  personalityColorLight = "#F3F4F6",
  personalityColorDark = "#111827",
  personalityColorMedium = "#E5E7EB",
  children,
  // keep in sync with Tailwind duration-300 (300ms)
  duration = 300,
  showCloseButton = true,
  isNewTest = false,
  autoScrollOnOpen = false,
  categories = [],
  onItemClick,
  assessmentIcons = [],
}) {
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(false);
  const scrollContainerRef = React.useRef(null);

  // Effect A: respond to isOpen changes (mount/unmount & start exit animation)
  useEffect(() => {
    let unmountTimer = null;
    if (isOpen) {
      // only set mounted here; activating happens in Effect B
      setMounted(true);
    } else {
      // start exit animation
      setActive(false);
      // unmount after animation completes
      unmountTimer = setTimeout(() => setMounted(false), duration);
    }
    return () => {
      if (unmountTimer) clearTimeout(unmountTimer);
    };
  }, [isOpen, duration]);

  // Effect B: when component is mounted, activate on next RAF tick so open animates
  useEffect(() => {
    if (!mounted) return undefined;

    let rafId = requestAnimationFrame(() => {
      // small extra tick to be safe across browsers
      // setTimeout 0 ensures paint; RAF alone often suffices
      setTimeout(() => setActive(true), 0);
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [mounted]);
  const { t } = useTranslation();
  // Gentle auto-scroll animation for the START A New Test popup.
  const hasAutoScrolledRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !autoScrollOnOpen || !isNewTest || !active) return;

    if (hasAutoScrolledRef.current) return;
    hasAutoScrolledRef.current = true;

    let startTimeout;
    let returnTimeout;
    let rafId;

    const smoothScrollTo = (element, target, duration = 500) => {
      const start = element.scrollTop;
      const distance = target - start;
      const startTime = performance.now();

      const easeInOut = (t) =>
        t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

      const step = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOut(progress);

        element.scrollTop = start + distance * easedProgress;

        if (progress < 1) {
          rafId = requestAnimationFrame(step);
        }
      };

      rafId = requestAnimationFrame(step);
    };

    const runScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      container.scrollTop = 0;

      startTimeout = setTimeout(() => {
        const maxScrollable = container.scrollHeight - container.clientHeight;

        const target = Math.min(
          maxScrollable,
          Math.round(container.clientHeight * 0.5),
        );

        if (target <= 0) return;

        // Scroll down
        smoothScrollTo(container, target, 1000);

        // Scroll back to top
        returnTimeout = setTimeout(() => {
          smoothScrollTo(container, 0, 1000);
        }, 500);
      }, 500);
    };

    runScroll();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (startTimeout) clearTimeout(startTimeout);
      if (returnTimeout) clearTimeout(returnTimeout);
    };
  }, [isOpen, autoScrollOnOpen, isNewTest, active]);

  // Reset flag when popup closes
  useEffect(() => {
    if (!isOpen) {
      hasAutoScrolledRef.current = false;
    }
  }, [isOpen]);

  if (!mounted) return null;

  const TestCategoryCard = ({
    categories = [],
    assessmentIcons = [],
    scrollRef,
  }) => {
    if (!categories || categories.length === 0) return null;

    return (
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden w-full"
      >
        <div className="bg-[#F8F8F8] rounded-xl p-4 pb-32 w-full shadow-md">
          {categories.map((section, index) => (
            <div key={section.title || index} className="w-full">
              {/* Category Title */}
              <h3 className="text-gray-900 text-sm font-semibold mb-4 text-left">
                {section.title}
              </h3>
              {/* Assessment Titles in this Category */}
              <div className="flex flex-col mb-2 items-start">
                {section.items && section.items.length > 0 ? (
                  section.items.map((item, itemIndex) => {
                    const matchingIcon = assessmentIcons.find(
                      (iconItem) =>
                        iconItem.key.trim().toLowerCase() ===
                        item.id.trim().toLowerCase(),
                    );

                    const IconComponent = matchingIcon
                      ? matchingIcon.icon
                      : null;

                    return (
                      <div
                        key={`${item.title}-${itemIndex}`}
                        className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 p-2 rounded-md transition w-full"
                        onClick={() => onItemClick && onItemClick(item.id)}
                      >
                        {IconComponent && (
                          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                            <img
                              src={IconComponent}
                              alt={item.title}
                              className="w-6 h-6 object-contain"
                              style={{ opacity: 0.5 }}
                            />
                          </div>
                        )}

                        <span className="text-gray-800 text-sm">
                          {item.title}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-sm">
                    {t("dashboard-b.personality.no_assessment")}
                  </p>
                )}
              </div>
              {/* Divider (Not for last section) */}
              {index !== categories.length - 1 && (
                <hr className="border-t border-gray-300 -mx-10 mb-2" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Bottom Sheet */}
      <div className="md:hidden">
        <div
          className={`fixed flex flex-col mx-auto w-full bottom-0 bg-[#F8F8F8] rounded-t-[20px] p-4 z-50
          max-h-[80vh]
          transform transition-transform duration-300 ease-in-out
          ${active ? "translate-y-0" : "translate-y-full"}`}
          style={{ willChange: "transform" }}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex justify-center mb-2">
            <button
              type="button"
              aria-label="Close sheet"
              className="w-28 h-1 bg-[#756B9B] rounded-full cursor-pointer"
              onClick={onClose}
              onTouchStart={onClose}
            />
          </div>

          <div className="flex justify-between items-center mb-4">
            <div className="text-left items-center px-2">
              <h2 className="text-xl font-bold">{title}</h2>
            </div>
            {showCloseButton && (
              <button
                id="mobile-test-panel-button"
                onClick={onClose}
                className="flex px-3 py-1 items-center min-w-max text-[16px] rounded-full text-white bg-[#805AED] border border-[#805AED]"
              >
                <span>{t("dashboard.close_button")}</span>
                <span className="text-[20px] ml-2">&times;</span>
              </button>
            )}
          </div>

          {/* Scrollable Body */}
          {categories && categories.length > 0 ? (
            <TestCategoryCard
              categories={categories}
              assessmentIcons={assessmentIcons}
              scrollRef={scrollContainerRef}
            />
          ) : (
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto pb-32"
            >
              {children}
            </div>
          )}
        </div>
      </div>

      {/* Overlay */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-opacity duration-300 ease-in-out
        ${active ? "opacity-50" : "opacity-0"}`}
        style={{ backgroundColor: "black" }}
        onClick={onClose}
      />
    </>
  );
}
